import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, LifeBuoy, LockOpen, Send } from "lucide-react";
import { api } from "../api";

export const AdminSupportPanel = ({ notify }) => {
  const [filter, setFilter] = useState("open");
  const [tickets, setTickets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (keepId = "") => {
    const { data } = await api.get(`/admin/support?status=${filter}`);
    setTickets(data);
    setSelectedId((current) => {
      const wanted = keepId || current;
      return data.some((ticket) => ticket.id === wanted) ? wanted : (data[0]?.id || "");
    });
  }, [filter]);
  useEffect(() => {
    load().catch(() => notify("Could not load support inbox"));
    const timer = window.setInterval(() => load().catch(() => null), 3000);
    return () => window.clearInterval(timer);
  }, [load, notify]);

  const selected = tickets.find((ticket) => ticket.id === selectedId);
  const sendReply = async (event) => {
    event.preventDefault();
    if (!reply.trim() || !selected) return;
    setBusy(true);
    try {
      await api.post(`/admin/support/${selected.id}/reply`, { message: reply.trim() });
      setReply("");
      notify("Support team reply sent");
      await load(selected.id);
    } catch (error) { notify(error.response?.data?.detail || "Reply failed"); }
    finally { setBusy(false); }
  };

  const changeStatus = async () => {
    if (!selected) return;
    const status = selected.status === "open" ? "closed" : "open";
    setBusy(true);
    try {
      await api.post(`/admin/support/${selected.id}/status`, { status });
      notify(status === "closed" ? "Support request closed" : "Support request reopened");
      await load(selected.id);
    } catch (error) { notify(error.response?.data?.detail || "Status update failed"); }
    finally { setBusy(false); }
  };

  return (
    <section className="tool-panel admin-support-panel" data-testid="admin-support-panel">
      <div className="section-label"><span>SUPPORT INBOX</span><span>{tickets.length} requests</span></div>
      <h2>Help users</h2>
      <p className="muted">Replies are always shown to users as coming from the Support team.</p>
      <div className="tabs" data-testid="admin-support-filter-tabs">
        {["open", "closed", "all"].map((item) => (
          <button className={filter === item ? "tab active" : "tab"} key={item} data-testid={`admin-support-filter-${item}`} onClick={() => setFilter(item)}>{item}</button>
        ))}
      </div>
      <div className="admin-support-layout">
        <div className="admin-support-list" data-testid="admin-support-ticket-list">
          {tickets.length === 0 ? <div className="support-empty" data-testid="admin-support-empty">No support requests here.</div> : tickets.map((ticket) => (
            <button className={`admin-support-ticket${selectedId === ticket.id ? " active" : ""}`} key={ticket.id} data-testid={`admin-support-ticket-${ticket.id}`} onClick={() => setSelectedId(ticket.id)}>
              <span>{ticket.user_name || ticket.user_identifier}</span>
              <strong>{ticket.subject}</strong>
              <small>{ticket.messages.length} messages · {ticket.status}</small>
            </button>
          ))}
        </div>
        <div className="admin-support-thread" data-testid="admin-support-thread">
          {!selected ? <div className="support-empty">Select a support request.</div> : (
            <>
              <div className="admin-support-thread-head">
                <div><strong>{selected.subject}</strong><span>{selected.user_name} · {selected.user_identifier}</span></div>
                <button className="ghost small" data-testid={`admin-support-status-${selected.id}`} disabled={busy} onClick={changeStatus}>
                  {selected.status === "open" ? <><CheckCircle2 size={14} /> Close</> : <><LockOpen size={14} /> Reopen</>}
                </button>
              </div>
              <div className="support-messages admin-thread-messages">
                {selected.messages.map((message) => (
                  <article className={`support-message ${message.sender}`} key={message.id} data-testid={`admin-support-message-${message.id}`}>
                    <div className="support-message-meta"><strong>{message.sender === "support" ? "Support team" : selected.user_name}</strong><time>{new Date(message.created_at).toLocaleString()}</time></div>
                    <p>{message.message}</p>
                  </article>
                ))}
              </div>
              {selected.status === "open" && (
                <form className="support-reply-form" onSubmit={sendReply} data-testid="admin-support-reply-form">
                  <label>Support team reply
                    <textarea data-testid="admin-support-reply-input" value={reply} maxLength={2000} onChange={(event) => setReply(event.target.value)} placeholder="Write a helpful response..." required />
                  </label>
                  <button className="primary" data-testid="admin-support-send-button" disabled={busy || !reply.trim()}><Send size={15} /> Send as Support team</button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};